'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack } from '@terra/ui-styled-system';
import { Globe02Icon } from '@terra/ui-icons';
import type {
  CellContextMenuEvent,
  ColDef,
  ICellRendererParams,
} from 'ag-grid-community';
import { Spinner } from '../../../components/spinner';
import { Table } from '../../../components/table';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import { AssetTabDrawerEmptyState } from './AssetTabDrawerEmptyState';
import {
  useEndpointWebpages,
  type EndpointRelatedWebpage,
} from '../hooks/useEndpointWebpages';
import { ASSET_TYPE_VALUES } from '../types';

type EndpointsWebpagesTabProps = {
  endpointId: string;
  onOpenRelatedAsset?: (asset: {
    id: string;
    type: typeof ASSET_TYPE_VALUES.WEBPAGE;
    title: string;
  }) => void;
  onRowContextMenu?: (
    event: CellContextMenuEvent<EndpointRelatedWebpage>,
  ) => void;
};
const webpageCellStyle = {
  display: 'flex',
  alignItems: 'center',
} as const;

export function EndpointsWebpagesTab({
  endpointId,
  onOpenRelatedAsset,
  onRowContextMenu,
}: EndpointsWebpagesTabProps) {
  const t = useTranslations('designSystem.applicationAssets.drawer.webpages');
  const { webpages, isLoading } = useEndpointWebpages({
    endpointId,
    enabled: true,
  });
  const columnDefs = useMemo<ColDef<EndpointRelatedWebpage>[]>(
    () => [
      {
        field: 'url',
        headerName: t('columnUrl'),
        flex: 2,
        sortable: true,
        cellStyle: webpageCellStyle,
        cellRenderer: (params: ICellRendererParams<EndpointRelatedWebpage>) => (
          <BodyDefault truncate>{params.data?.url}</BodyDefault>
        ),
      },
      {
        field: 'role',
        headerName: t('columnRole'),
        flex: 1,
        sortable: true,
        cellStyle: webpageCellStyle,
        cellRenderer: (params: ICellRendererParams<EndpointRelatedWebpage>) => (
          <MetaDefault color="fg.muted">{params.data?.role || '—'}</MetaDefault>
        ),
      },
      {
        field: 'description',
        headerName: t('columnDescription'),
        flex: 2,
        sortable: false,
        cellStyle: webpageCellStyle,
        cellRenderer: (params: ICellRendererParams<EndpointRelatedWebpage>) => (
          <MetaDefault color="fg.muted" truncate>
            {params.data?.description || '—'}
          </MetaDefault>
        ),
      },
    ],
    [t],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <Spinner />
      </Box>
    );
  }

  if (webpages.length === 0) {
    return (
      <AssetTabDrawerEmptyState
        icon={Globe02Icon}
        title={t('emptyTitle')}
        description={t('emptyDescription')}
      />
    );
  }

  return (
    <VStack gap={4} alignItems="stretch">
      <Table
        rowData={webpages}
        columnDefs={columnDefs}
        getRowId={(params) => params.data.id}
        onRowClicked={(event) => {
          if (!event.data?.id || !onOpenRelatedAsset) {
            return;
          }

          onOpenRelatedAsset({
            id: event.data.id,
            type: ASSET_TYPE_VALUES.WEBPAGE,
            title: event.data.url,
          });
        }}
        onCellContextMenu={(event) => {
          if (!event.data || !onRowContextMenu) {
            return;
          }

          onRowContextMenu(event);
        }}
        preventDefaultOnContextMenu={Boolean(onRowContextMenu)}
        suppressCellFocus
        domLayout="autoHeight"
      />
    </VStack>
  );
}
